import {
  PathType,
  RoutesAirportsType,
  GroupedRoutesAirportsType,
  CompletedRoute,
  GrouppedRoute,
} from './types'
import { getMinMaxTime } from './utils'

const sortPathNames = (path: PathType[]) =>
  path.map((p) => p.name).sort((a, b) => a.localeCompare(b))

export const groupRoutes = ({
  routes,
  airports,
}: RoutesAirportsType): GroupedRoutesAirportsType => {
  const distinctiveRoutes = routes.map<CompletedRoute>((routeObj) => {
    const isOvernight = routeObj.flights.some((flight) => flight.overnight)
    const sortedJoinedPath = sortPathNames(routeObj.path).join('')
    return {
      ...routeObj,
      id: sortedJoinedPath + routeObj.n_tickets + Number(isOvernight),
      overnight: isOvernight,
    }
  })

  const groups = distinctiveRoutes.reduce((acc: Record<string, CompletedRoute[]>, route) => {
    const accRoutes = acc[route.id] || []
    return { ...acc, [route.id]: accRoutes.concat(route) }
  }, {})

  const sortedGroupsByOptimalityScore = Object.values(groups).map((group) =>
    group.sort((elementA, elementB) => elementA.optimality_score - elementB.optimality_score)
  )

  const groupAgentsWithCarriers = sortedGroupsByOptimalityScore.map<GrouppedRoute>((group) => {
    const groupWithCarriers: CompletedRoute[] = group.slice(0).map((route) => ({
      ...route,
      flights: [...route.flights].map((flight) =>
        flight.distance === null
          ? flight
          : {
              ...flight,
              carriers: [{ carrier: flight.code.slice(0, 2), carrier_name: flight.carrier_name }],
            }
      ),
    }))

    const groupAgentWithHightestScore = groupWithCarriers[0]
    const restGroup = groupWithCarriers.slice(1)
    const groupAgentWithRestCarriers = restGroup.length
      ? restGroup.reduce(
          (acc, route) => ({
            ...acc,
            flights: acc.flights.map((flight, i) => ({
              ...flight,
              carriers: [...flight.carriers, ...route.flights[i].carriers],
            })),
          }),
          groupAgentWithHightestScore
        )
      : groupAgentWithHightestScore

    const groupAgentWithUniqueCarriers: CompletedRoute = {
      ...groupAgentWithRestCarriers,
      flights: [...groupAgentWithRestCarriers.flights].map((flight) => ({
        ...flight,
        carriers: [...flight.carriers].filter(
          (carrier, i, arr) =>
            arr.findIndex((carrier2) => carrier2.carrier === carrier.carrier) === i
        ),
      })),
    }

    const groupMinMaxTime = getMinMaxTime(group)
    const groupSize = group.length

    return { ...groupAgentWithUniqueCarriers, groupMinMaxTime, groupSize }
  })

  const sortedAgentsByOptimalityScore = groupAgentsWithCarriers.sort(
    (agentA, agentB) => agentA.optimality_score - agentB.optimality_score
  )

  return { routes: sortedAgentsByOptimalityScore, airports }
}
